import { useEffect, useState, Fragment } from "react";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";
import { SnapshotBar } from "./Metrics";

function KthReadiness({ fw, user, lang, t, onUpdated, readOnly }) {
  const dim = fw.dimensions[0];
  const initial = user?.kth_readiness || {};
  const [levels, setLevels] = useState(initial);

  const setLevel = async (area, level) => {
    if (readOnly) return;
    setLevels((c) => ({ ...c, [area]: level }));
    try {
      await api.post("/me/kth-readiness", { area, level });
      if (onUpdated) onUpdated();
    } catch (e) {
      /* noop */
    }
  };

  return (
    <div className="tb-block" style={{ marginBottom: "1.5rem" }} data-testid="kth-self-assessment">
      <h3 style={{ fontFamily: "var(--font-display)", margin: "0 0 0.4rem" }}>{t("framework.kthSelfTitle")}</h3>
      <p className="tb-help" style={{ marginTop: 0 }}>{t("framework.kthSelfHelp")}</p>
      <div className="tb-kth-stack">
        {dim.attributes.map((attr) => {
          const sel = levels[attr.key];
          return (
            <div className="tb-kth-row" key={attr.key} data-testid={`kth-row-${attr.key}`}>
              <div className="tb-kth-row-label">
                {lang === "fr" ? attr.name_fr || attr.name_en : attr.name_en}
              </div>
              <div className="tb-kth-buttons">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`tb-kth-btn ${sel === n ? "sel" : ""} ${sel && n <= sel ? "filled" : ""}`}
                    onClick={() => setLevel(attr.key, n)}
                    disabled={readOnly}
                    style={readOnly ? { cursor: "default" } : undefined}
                    data-testid={`kth-${attr.key}-${n}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              {sel && (() => {
                const cellRaw = (fw.cells || {})[`${dim.key}::${attr.key}::l${sel}`];
                const cell = typeof cellRaw === "string" ? { en: cellRaw, fr: "" } : cellRaw || {};
                const txt = cell[lang] || cell[lang === "fr" ? "en" : "fr"] || "";
                return txt ? <div className="tb-kth-current">{txt}</div> : null;
              })()}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Cell({ fw, dim, attr, stage, isAdmin, lang, onSaved }) {
  const key = `${dim.key}::${attr.key}::${stage.key}`;
  const cellRaw = (fw.cells || {})[key];
  // Support both legacy string and new {en, fr} shape
  const cell = typeof cellRaw === "string" ? { en: cellRaw, fr: "" } : cellRaw || { en: "", fr: "" };
  const initial = cell[lang] || cell[lang === "fr" ? "en" : "fr"] || "";
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(cell[lang] || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/frameworks/${fw.id}/cell`, {
        dimension_key: dim.key,
        attribute_key: attr.key,
        stage_key: stage.key,
        text: value,
        lang,
      });
      setEditing(false);
      onSaved();
    } catch (e) {
      /* noop */
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin || !editing) {
    return (
      <td
        className="tb-fw-cell"
        onClick={isAdmin ? () => { setValue(cell[lang] || ""); setEditing(true); } : undefined}
        data-testid={`fw-cell-${key}`}
        style={{ cursor: isAdmin ? "pointer" : "default" }}
      >
        {initial ? (
          <span style={{ whiteSpace: "pre-line" }}>{initial}</span>
        ) : (
          isAdmin ? <span className="tb-muted">+ add</span> : <span className="tb-muted">—</span>
        )}
      </td>
    );
  }
  return (
    <td className="tb-fw-cell editing">
      <textarea value={value} onChange={(e) => setValue(e.target.value)} rows={4} data-testid={`fw-cell-edit-${key}`} />
      <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
        <button className="tb-primary-btn" style={{ padding: "4px 10px", fontSize: "0.78rem" }} onClick={save} disabled={saving}>
          ✓
        </button>
        <button className="tb-ghost-btn" style={{ padding: "4px 10px", fontSize: "0.78rem" }} onClick={() => { setValue(cell[lang] || ""); setEditing(false); }}>
          ×
        </button>
      </div>
    </td>
  );
}

export default function Framework({ targetUserId, hideHeader, snapshotInline, focusSlug }) {
  const { user, refresh } = useAuth();
  const { t, lang } = useI18n();
  const [frameworks, setFrameworks] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [stagePicked, setStagePicked] = useState(user?.framework_stage || "");
  const [err, setErr] = useState("");
  const [snaps, setSnaps] = useState([]);
  const [snapBusy, setSnapBusy] = useState(false);
  const [targetUser, setTargetUser] = useState(null);

  const viewingOther = !!targetUserId && targetUserId !== user?.id;

  const load = () => {
    api.get("/frameworks").then((r) => {
      setFrameworks(r.data);
      setActiveId((cur) => {
        const focused = focusSlug && r.data.find((f) => f.slug === focusSlug);
        if (focused) return focused.id;
        if (cur) return cur;
        return user?.framework_id || r.data[0]?.id || null;
      });
    }).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  };
  const loadSnaps = () => {
    const q = targetUserId && targetUserId !== user?.id ? `?user_id=${targetUserId}` : "";
    api.get(`/snapshots/framework${q}`).then((r) => setSnaps(r.data)).catch(() => setSnaps([]));
  };
  const loadTargetUser = () => {
    if (!viewingOther) { setTargetUser(null); return; }
    api.get(`/users/${targetUserId}/profile`).then((r) => setTargetUser(r.data)).catch(() => setTargetUser(null));
  };

  useEffect(() => { load(); loadSnaps(); loadTargetUser(); }, [activeId, user, targetUserId, focusSlug]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!frameworks || !user) return <div className="tb-loading">{t("common.loading")}</div>;
  const fw = frameworks.find((f) => f.id === activeId) || frameworks[0];
  if (!fw) return <div className="tb-page"><p className="tb-muted">{t("framework.empty")}</p></div>;

  const isAdmin = user.role === "admin";
  const isEntrepreneur = user.role === "entrepreneur";
  const targetIsFounder = viewingOther && targetUser?.role === "entrepreneur";
  const founderStage = viewingOther ? (targetUser?.framework_stage || "") : stagePicked;
  const founderKth = viewingOther ? (targetUser || {}) : user;
  const title = lang === "fr" ? fw.title_fr || fw.title_en : fw.title_en;
  const desc = lang === "fr" ? fw.description_fr || fw.description_en : fw.description_en;

  const pickStage = async (key) => {
    setStagePicked(key);
    try {
      await api.post("/me/framework-stage", { framework_id: fw.id, stage_key: key });
      if (refresh) refresh();
    } catch (e) {
      /* noop */
    }
  };

  return (
    <div className="tb-page">
      {!hideHeader && (
        <>
          <h1 className="tb-h1" data-testid="framework-heading">{t("framework.title")}</h1>
          <p className="tb-muted" style={{ marginBottom: "1.5rem", maxWidth: 760 }} data-testid="framework-subtitle">
            {t("framework.subtitle")}
          </p>
        </>
      )}

      {(isEntrepreneur || snapshotInline || viewingOther) && (
        <SnapshotBar
          kind="framework"
          snaps={snaps}
          canSave={isEntrepreneur && (!targetUserId || targetUserId === user.id)}
          busy={snapBusy}
          t={t}
          userId={viewingOther ? targetUserId : undefined}
          onSave={async () => {
            setSnapBusy(true);
            try { await api.post("/snapshots/framework"); loadSnaps(); } finally { setSnapBusy(false); }
          }}
        />
      )}

      {frameworks.length > 1 && (
        <>
          <h2 className="tb-h2" style={{ margin: "0 0 0.85rem", paddingTop: "2.25rem" }} data-testid="fw-assigned-title">{t("framework.assignedTitle")}</h2>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {frameworks.map((f) => (
              <button
                key={f.id}
                className={f.id === activeId ? "tb-primary-btn" : "tb-ghost-btn"}
                style={{ padding: "8px 14px", fontSize: "0.84rem" }}
                onClick={() => setActiveId(f.id)}
                data-testid={`fw-tab-${f.slug}`}
              >
                {lang === "fr" ? f.title_fr || f.title_en : f.title_en}
              </button>
            ))}
          </div>
        </>
      )}

      <h2 className="tb-h2" style={{ margin: "0 0 0.45rem", paddingTop: "2.75rem" }} data-testid="fw-active-title">{title}</h2>
      {desc ? <p className="tb-muted" style={{ margin: "0 0 0.9rem" }}>{desc}</p> : null}

      {((isEntrepreneur && !viewingOther) || targetIsFounder) && fw.slug !== "kth-innovation-readiness-level" && fw.slug !== "mit-disciplined-entrepreneurship" && !fw.owner_id && (
        <div className="tb-block" style={{ marginBottom: "1.5rem" }} data-testid="fw-stage-box">
          <p className="tb-muted" style={{ margin: "0 0 0.8rem" }} data-testid="fw-choose-stage-hint">{t("framework.chooseStageHint")}</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {fw.stages.map((s) => {
              const isPicked = (viewingOther ? founderStage : stagePicked) === s.key;
              return (
                <button
                  key={s.key}
                  className={isPicked ? "tb-primary-btn" : "tb-ghost-btn"}
                  onClick={() => !viewingOther && pickStage(s.key)}
                  disabled={viewingOther}
                  data-testid={`fw-pick-stage-${s.key}`}
                  style={{ padding: "10px 16px", cursor: viewingOther ? "default" : "pointer" }}
                >
                  <span style={{ marginRight: 8 }}>{s.icon}</span>
                  {lang === "fr" ? s.name_fr || s.name_en : s.name_en}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {((isEntrepreneur && !viewingOther) || targetIsFounder) && fw.slug === "kth-innovation-readiness-level" && (
        <KthReadiness fw={fw} user={founderKth} lang={lang} t={t} onUpdated={refresh} readOnly={viewingOther} />
      )}

      {fw.slug === "mit-disciplined-entrepreneurship" || fw.owner_id ? (
        <MitChecklist fw={fw} t={t} lang={lang} targetUserId={targetUserId} me={user} />
      ) : (
        <div className="tb-fw-table-wrap" data-testid="framework-table">
          <table className="tb-fw-table">
            <thead>
              <tr>
                <th></th>
                {fw.stages.map((s) => (
                  <th key={s.key} className={(viewingOther ? founderStage : stagePicked) === s.key ? "tb-fw-current" : ""}>
                    <span style={{ marginRight: 6 }}>{s.icon}</span>
                    {lang === "fr" ? s.name_fr || s.name_en : s.name_en}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {fw.dimensions.map((dim) => (
                <Fragment key={dim.key}>
                  <tr className="tb-fw-section">
                    <th colSpan={fw.stages.length + 1}>
                      {lang === "fr" ? dim.name_fr || dim.name_en : dim.name_en}
                    </th>
                  </tr>
                  {dim.attributes.map((attr) => (
                    <tr key={`${dim.key}-${attr.key}`}>
                      <th className="tb-fw-row-h">
                        {lang === "fr" ? attr.name_fr || attr.name_en : attr.name_en}
                      </th>
                      {fw.stages.map((stage) => (
                        <Cell key={stage.key} fw={fw} dim={dim} attr={attr} stage={stage} isAdmin={isAdmin} lang={lang} onSaved={load} />
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {err && <div className="tb-form-error" style={{ marginTop: 12 }}>{err}</div>}

      {(fw.references && fw.references.length > 0) || fw.legal_notice_en || fw.legal_notice_fr ? (
        <div className="tb-block" style={{ marginTop: 24, background: "transparent" }} data-testid="fw-footer">
          {fw.references && fw.references.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <h4 style={{ margin: "0 0 8px", fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--muted)" }}>
                {t("fwBuilder.references")}
              </h4>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: "0.88rem" }}>
                {fw.references.map((r, i) => (
                  <li key={i} style={{ marginBottom: 4 }} data-testid={`fw-ref-link-${i}`}>
                    <a href={r.url} target="_blank" rel="noreferrer">{r.label || r.url}</a>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(fw.legal_notice_en || fw.legal_notice_fr) && (
            <div>
              <h4 style={{ margin: "0 0 8px", fontSize: "0.82rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--muted)" }}>
                {t("fwBuilder.legalNotice")}
              </h4>
              <p className="tb-muted" style={{ fontSize: "0.82rem", whiteSpace: "pre-line", margin: 0 }} data-testid="fw-legal-text">
                {(lang === "fr" ? fw.legal_notice_fr : fw.legal_notice_en) || fw.legal_notice_en || fw.legal_notice_fr}
              </p>
            </div>
          )}
          {fw.author_name && (
            <p className="tb-muted" style={{ fontSize: "0.78rem", marginTop: 12, marginBottom: 0 }} data-testid="fw-author-line">
              {t("fwList.by")} {fw.author_name}
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}

function MitChecklist({ fw, t, lang, targetUserId, me }) {
  const [answers, setAnswers] = useState({});
  const [open, setOpen] = useState(null);
  const [err, setErr] = useState("");
  const isOwn = !targetUserId || targetUserId === me.id;
  const editable = me.role === "entrepreneur" && isOwn;

  const load = () => {
    const q = !isOwn ? `?user_id=${targetUserId}` : "";
    api.get(`/framework-steps?framework_id=${fw.id}${q && q.replace("?", "&")}`)
      .then((r) => {
        const by = {};
        (r.data || []).forEach((a) => { by[a.step_id] = a; });
        setAnswers(by);
      })
      .catch(() => setAnswers({}));
  };
  useEffect(() => { load(); }, [fw.id, targetUserId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const stageLabel = (key) => {
    const s = fw.stages.find((x) => x.key === key);
    if (!s) return "—";
    return lang === "fr" ? s.name_fr || s.name_en : s.name_en;
  };
  const stageColor = (key) => key === "validated" ? "good" : key === "in_progress" ? "warn" : "";

  const counts = { todo: 0, in_progress: 0, validated: 0 };
  fw.dimensions.forEach((dim) => dim.attributes.forEach((attr) => {
    const a = answers[`${dim.key}::${attr.key}`];
    const st = a?.status || "todo";
    if (counts[st] != null) counts[st]++;
  }));
  const total = fw.dimensions.reduce((n, d) => n + d.attributes.length, 0);

  return (
    <div data-testid="mit-checklist">
      <div className="tb-block" style={{ marginBottom: "1rem", display: "flex", gap: 18, flexWrap: "wrap", alignItems: "center" }}>
        <div>
          <div className="tb-muted" style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>{t("mit.progress")}</div>
          <div style={{ fontWeight: 700, fontSize: "1.05rem", marginTop: 4 }}>{counts.validated} / {total} {t("mit.validated").toLowerCase()}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <span className="tb-badge good">✓ {counts.validated}</span>
          <span className="tb-badge warn">… {counts.in_progress}</span>
          <span className="tb-badge">{counts.todo} {t("mit.todo").toLowerCase()}</span>
        </div>
      </div>

      {fw.dimensions.map((dim) => (
        <div key={dim.key} style={{ marginBottom: "1.5rem" }}>
          <h3 style={{ fontFamily: "var(--font-display)", margin: "0 0 0.6rem", fontSize: "1rem", color: "var(--accent)" }}>
            {lang === "fr" ? dim.name_fr || dim.name_en : dim.name_en}
          </h3>
          {dim.attributes.map((attr) => {
            const sid = `${dim.key}::${attr.key}`;
            const a = answers[sid];
            const form = (fw.step_forms || {})[sid];
            const status = a?.status || "todo";
            const isOpen = open === sid;
            return (
              <div key={sid} className="tb-block" style={{ marginBottom: 8, padding: 0 }} data-testid={`mit-step-${sid}`}>
                <button
                  onClick={() => setOpen(isOpen ? null : sid)}
                  style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", background: "transparent", border: 0, color: "inherit", padding: "14px 16px", cursor: "pointer", textAlign: "left" }}
                  data-testid={`mit-step-toggle-${sid}`}
                >
                  <span style={{ fontWeight: 600 }}>{lang === "fr" ? attr.name_fr || attr.name_en : attr.name_en}</span>
                  <span className={`tb-badge ${stageColor(status)}`} data-testid={`mit-step-status-${sid}`}>{stageLabel(status)}</span>
                </button>
                {isOpen && (
                  <StepForm
                    key={sid + (a?.updated_at || "")}
                    sid={sid}
                    fw={fw}
                    form={form}
                    existing={a}
                    editable={editable}
                    t={t}
                    lang={lang}
                    onSaved={(rec) => { setAnswers((prev) => ({ ...prev, [sid]: rec })); }}
                    onError={setErr}
                  />
                )}
              </div>
            );
          })}
        </div>
      ))}
      {err && <div className="tb-form-error" style={{ marginTop: 12 }}>{err}</div>}
    </div>
  );
}

function StepForm({ sid, fw, form, existing, editable, t, lang, onSaved, onError }) {
  const [status, setStatus] = useState(existing?.status || "todo");
  const [answers, setAnswers] = useState(existing?.answers || {});
  const [deliverable, setDeliverable] = useState(existing?.deliverable || "");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/framework-steps/${fw.id}/${sid}`, { status, answers, deliverable });
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
      onSaved(data);
    } catch (e) {
      onError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: "0 16px 16px 16px", borderTop: "1px solid var(--border)" }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12 }} data-testid={`mit-step-statuses-${sid}`}>
        {fw.stages.map((s) => (
          <button
            key={s.key}
            className={`tb-chip ${status === s.key ? "active" : ""}`}
            onClick={() => editable && setStatus(s.key)}
            disabled={!editable}
            data-testid={`mit-step-set-${sid}-${s.key}`}
          >
            {lang === "fr" ? s.name_fr || s.name_en : s.name_en}
          </button>
        ))}
      </div>

      {form?.questions?.length > 0 && (
        <div style={{ marginTop: 14 }}>
          {form.questions.map((q) => (
            <div key={q.key} style={{ marginBottom: 12 }}>
              <label style={{ fontSize: "0.84rem", fontWeight: 600 }}>
                {lang === "fr" ? q.label_fr || q.label_en : q.label_en}
              </label>
              <textarea
                rows={3}
                value={answers[q.key] || ""}
                onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })}
                disabled={!editable}
                placeholder={editable ? t("mit.placeholder") : "—"}
                style={{ width: "100%", marginTop: 4 }}
                data-testid={`mit-step-q-${sid}-${q.key}`}
              />
            </div>
          ))}
        </div>
      )}

      {form && (
        <div style={{ marginTop: 8 }}>
          <label style={{ fontSize: "0.84rem", fontWeight: 600 }}>
            {t("mit.deliverable")}: <span className="tb-muted" style={{ fontWeight: 400 }}>{lang === "fr" ? form.deliverable_fr : form.deliverable_en}</span>
          </label>
          <textarea
            rows={3}
            value={deliverable}
            onChange={(e) => setDeliverable(e.target.value)}
            disabled={!editable}
            placeholder={editable ? t("mit.deliverablePlaceholder") : "—"}
            style={{ width: "100%", marginTop: 4 }}
            data-testid={`mit-step-deliverable-${sid}`}
          />
        </div>
      )}

      {form && (
        <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div className="tb-muted" style={{ fontSize: "0.82rem" }}>
            <b>{t("mit.metric")}:</b> {lang === "fr" ? form.metric_fr : form.metric_en}
          </div>
          <div className="tb-muted" style={{ fontSize: "0.82rem" }}>
            <b>{t("mit.gate")}:</b> {lang === "fr" ? form.gate_fr : form.gate_en}
          </div>
        </div>
      )}

      {editable && (
        <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center" }}>
          <button className="tb-primary-btn" onClick={save} disabled={busy} data-testid={`mit-step-save-${sid}`}>
            {busy ? t("auth.loading") : t("mit.saveStep")}
          </button>
          {saved && <span className="tb-badge good" data-testid={`mit-step-saved-${sid}`}>✓ {t("settings.saved")}</span>}
        </div>
      )}
    </div>
  );
}

